<template>
  <v-container fluid>
    <v-row class="d-flex flex-row">
      <v-col cols="12">
        <FormPageDictionary
          :data="dictionary"
          :loading="isMutationLoading"
          :loadingData="isFormLoading"
          :isNew="!id"
          :language="language"
          @language-update="language = $event"
          @submit="formDictionarySubmit"
          @delete-dictionary="deleteDictionary"
        />
      </v-col>
    </v-row>
    <ConfirmationDialog ref="confirmDictionaryRemoving" confirmText="Удалить" />
  </v-container>
</template>

<script>
import FormPageDictionary from './form'
import ConfirmationDialog from '@/components/confirmation-dialog'

import createDictionary from '@/services/gql/types/Dictionaries/mutations/createDictionary.gql'
import deleteDictionary from '@/services/gql/types/Dictionaries/mutations/deleteDictionary.gql'
import updateDictionary from '@/services/gql/types/Dictionaries/mutations/updateDictionary.gql'
import dictionaryQuery from '@/services/gql/types/Dictionaries/queries/dictionary.gql'
import createDictionaryOptions from '@/services/gql/types/DictionaryOptions/mutations/createDictionaryOptions.gql'
import deleteDictionaryOptions from '@/services/gql/types/DictionaryOptions/mutations/deleteDictionaryOptions.gql'
import updateDictionaryOptions from '@/services/gql/types/DictionaryOptions/mutations/updateDictionaryOptions.gql'
import { contentLocales, contentLocalesValues } from '@/configs/locales'
import { getListWithNewMainLanguage, setTranslation } from '@/utils/locales'

export default {
  name: 'PageDictionary',
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    FormPageDictionary,
    ConfirmationDialog
  },
  metaInfo () {
    return {
      title: this.id ? 'Редактировать справочник' : 'Создать справочник'
    }
  },
  data () {
    return {
      language: contentLocales[0].value,
      dictionary: null
    }
  },
  computed: {
    isMutationLoading () {
      return (
        this.$async.createDictionary.$pending ||
        this.$async.updateDictionary.$pending ||
        this.$async.createDictionaryOptions.$pending ||
        this.$async.updateDictionaryOptions.$pending
      )
    },
    isFormLoading () {
      return this.$apollo.queries.dictionary.loading
    }
  },
  methods: {
    async refetchDictionary () {
      await this.$apollo.queries.dictionary.refetch()
    },
    formDictionarySubmit (dictionaryPayload) {
      if (!this.id) this.createDictionary(dictionaryPayload)
      else this.updateDictionary(dictionaryPayload)
    },

    async createDictionary (dictionaryPayload) {
      const {
        data: { dictionaryOptions, label, name }
      } = dictionaryPayload

      try {
        const {
          data: { createDictionary }
        } = await this.$async.createDictionary.$perform({
          data: {
            label,
            name
          }
        })
        for (const dictionaryOption of dictionaryOptions) {
          if (createDictionary.id) {
            await this.changeDictionaryOptions(createDictionary.id, dictionaryOption.data)
          }
        }
        this.$successMsg('Справочник создан')
        await this.$router.push({ name: 'page-dictionaries' })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании справочника')
      }
    },
    async createDictionaryOptions (dictionaryId, { label, value }) {
      const data = {
        dictionaryId,
        label,
        value
      }
      await this.$async.createDictionaryOptions.$perform({
        data
      })
    },

    async changeDictionaryOptions (dictionaryId, dictionaryOptionPayload) {
      if (dictionaryOptionPayload.id) {
        await this.updateDictionaryOptions(dictionaryId, dictionaryOptionPayload)
      } else {
        await this.createDictionaryOptions(dictionaryId, dictionaryOptionPayload)
      }
    },
    async deleteDictionary () {
      try {
        await this.$refs.confirmDictionaryRemoving.open()
        for (const dictionaryOption of this.dictionary.options) {
          await this.deleteDictionaryOptions(dictionaryOption.id)
        }
        await this.$async.deleteDictionary.$perform({ id: Number(this.id) })
        this.$successMsg('Справочник удален')
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, `Ошибка при удалении справочника с ID ${this.id}`)
      } finally {
        await this.$router.push({ name: 'page-dictionaries' })
      }
    },
    async deleteDictionaryOptions (dictionaryOptionsId) {
      this.$async.deleteDictionaryOptions.$perform({ id: dictionaryOptionsId })
    },
    async updateDictionary ({ data, postActions }) {
      const { dictionaryOptions, ...payload } = data
      try {
        setTranslation(data, this.language)
        // delete dictionary options
        for (const optionToRemove of postActions.itemsToRemove) {
          if (optionToRemove) {
            await this.deleteDictionaryOptions(optionToRemove)
          }
        }
        await this.$async.updateDictionary.$perform(
          {
            id: this.id,
            data: {
              ...payload
            }
          },
          { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) }
        )
        // update dictionary options
        for (const dictionaryOption of dictionaryOptions) {
          if (this.id) {
            await this.changeDictionaryOptions(this.id, dictionaryOption.data)
          }
        }

        await this.refetchDictionary()
        this.$successMsg('Справочник обновлен')
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при обновлении справочника')
      }
    },
    async updateDictionaryOptions (dictionaryId, payload) {
      payload.translations = {}
      setTranslation(payload, this.language)
      const { id, ...data } = payload
      await this.$async.updateDictionaryOptions.$perform(
        {
          id,
          data
        },
        { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) }
      )
    }
  },
  apollo: {
    dictionary: {
      query: dictionaryQuery,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            id: Number(this.id)
          }
        }
      },
      context () {
        return {
          headers: {
            Language: getListWithNewMainLanguage(this.language, contentLocalesValues())
          }
        }
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-blog-posts' })
        }
        const { options, ...other } = res?.data?.dictionary
        this.dictionary = {
          ...other,
          dictionaryOptions: [...options]
        }
      }
    }
  },
  async: {
    createDictionary (variables) {
      return this.$apollo.mutate({
        mutation: createDictionary,
        variables
      })
    },
    deleteDictionary (variables) {
      return this.$apollo.mutate({
        mutation: deleteDictionary,
        variables
      })
    },
    updateDictionary (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateDictionary,
        variables,
        context: {
          headers
        }
      })
    },
    createDictionaryOptions (variables) {
      return this.$apollo.mutate({
        mutation: createDictionaryOptions,
        variables
      })
    },
    deleteDictionaryOptions (variables) {
      return this.$apollo.mutate({
        mutation: deleteDictionaryOptions,
        variables
      })
    },
    updateDictionaryOptions (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateDictionaryOptions,
        variables,
        context: {
          headers
        }
      })
    }
  }
}
</script>
